import React, { Suspense, lazy } from "react";
import { Provider } from "react-redux";
import { store } from "../../configurations";
import ReactDOM from "react-dom";
import Spinner from "../../components/Spinner/LoadingSpinner";
import MobileSubLayout from "../../layouts/MobileSubLayout";
import { ORDER_MODE } from "@/constants/app";
import MobileDrawerLayout from "../../layouts/MobileDrawerLayout";
import { iTradeEnv } from "@/configurations/app-configs/iTrade.env";

const original = {
  OrderConfirmMobile: lazy(() =>
    import("../../components/OrderConfirm/OrderConfirm.mobile")
  ),
  OrderConfirmStatus: lazy(() =>
    import(`../../components/OrderConfirm/OrderConfirmStatus.mobile`)
  ),
  OrderCancel: lazy(() =>
    import(`../../components/OrderCancel/OrderCancel.mobile`)
  ),
  OrderJournalDetail: lazy(() =>
    import("../../components/OrderJournalDetail/OrderJournalDetail.mobile")
  ),
  OrderHistory: lazy(() =>
    import("../../components/OrderTable/OrderHistory.mobile")
  ),
  Portfolio: lazy(() =>
    import("../../components/MyAccount/Portfolio/Portfolio.mobile")
  ),
  FundManagementWithdrawalMobile: lazy(() =>
    import("../../components/FundManagement/FundManagementWithdrawal.mobile")
  ),
  ExchangeInstructionMobile: lazy(() =>
    import("../../components/FundManagement/ExchangeInstruction.mobile")
  ),
};

const rewrites = {
  OrderConfirmMobile: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderConfirm/OrderConfirm.mobile`
    )
  ),
  OrderConfirmStatus: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderConfirm/OrderConfirmStatus.mobile`
    )
  ),
  OrderCancel: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderCancel/OrderCancel.mobile`
    )
  ),
  OrderJournalDetail: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderJournalDetail/OrderJournalDetail.mobile`
    )
  ),
  OrderHistory: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/OrderTable/OrderHistory.mobile`
    )
  ),
  Portfolio: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/MyAccount/Portfolio/Portfolio.mobile`
    )
  ),
  FundManagementWithdrawalMobile: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/FundManagement/FundManagementWithdrawal.mobile`
    )
  ),
  ExchangeInstructionMobile: lazy(() =>
    import(
      `@/companys/${iTradeEnv.company}/components/FundManagement/ExchangeInstruction.mobile`
    )
  ),
};

const InstrumentMobile = lazy(() =>
  import("../../components/Instrument/Instrument.mobile")
);
const CADetailMobile = lazy(() =>
  import("../../components/@app/CorpAction/CorporateActionDetail.mobile")
);
const ApplyPlan = lazy(() =>
  import("../../components/@app/IPO/ApplyPlan.mobile")
);
const AllotmentResultEnquiry = lazy(() =>
  import("../../components/@app/IPO/AllotmentResultEnquiry.mobile")
);
const PlaceOrderMobile = lazy(() =>
  import("../../components/Instrument/PlaceOrder.mobile")
);
const OrderBookMobile = lazy(() =>
  import("../../components/OrderTable/OrderBook.mobile")
);
const OrderCancelMobile = iTradeEnv?.pages?.trade?.ordercancel?.rewrite
  ? rewrites.OrderCancel
  : original.OrderCancel;
const OrderHistoryMobile = iTradeEnv?.pages?.trade?.orderHistory?.rewrite
  ? rewrites.OrderHistory
  : original.OrderHistory;
const PortfolioMobile = iTradeEnv?.pages?.myAccount?.portfolio?.rewrite
  ? rewrites.Portfolio
  : original.Portfolio;
const OrderConfirmMobile = iTradeEnv?.pages?.trade?.orderconfirm?.rewrite
  ? rewrites.OrderConfirmMobile
  : original.OrderConfirmMobile;
const OrderConfirmStatusMobile = iTradeEnv?.pages?.trade?.orderconfirmstatus
  ?.rewrite
  ? rewrites.OrderConfirmStatus
  : original.OrderConfirmStatus;
const OrderJournalDetailMobile = iTradeEnv?.pages?.trade?.orderJournal?.rewrite
  ? rewrites.OrderJournalDetail
  : original.OrderJournalDetail;
const OrderModifyMobile = lazy(() =>
  import("../../components/OrderDialog/OrderModify.mobile")
);
const TransactionHistoryMobile = lazy(() =>
  import(
    "../../components/MyAccount/TransactionHistory/TransactionHistory.mobile"
  )
);
const SubAccount = lazy(() => import("../../components/Account/SubAccount"));
const PersonalSetting = lazy(() =>
  import("../../components/PersonalSetting/PersonalSetting.mobile")
);
const ChangePassword = lazy(() =>
  import("../../components/PersonalSetting/ChangePassword.mobile")
);
const SwitchAccount = lazy(() =>
  import("../../components/PersonalSetting/SwitchAccount.mobile")
);
const FundManagementDWMobile = lazy(() =>
  import("../../components/FundManagement/FundManagementDW.mobile")
);
const BankSecuritiesTransferMobile = lazy(() =>
  import("../../components/FundManagement/BankSecuritiesTransfer.mobile")
);
// const ExchangeInstructionMobile = lazy(() => import('../../components/FundManagement/ExchangeInstruction.mobile'));
// const FundManagementWithdrawalMobile = lazy(() => import('../../components/FundManagement/FundManagementWithdrawal.mobile'));
const ExchangeInstructionMobile = iTradeEnv?.pages?.fundManagement
  ?.exchangeInstructionMobile
  ? rewrites.ExchangeInstructionMobile
  : original.ExchangeInstructionMobile;
const FundManagementWithdrawalMobile = iTradeEnv?.pages?.fundManagement
  ?.fundManagementWithdrawalMobile
  ? rewrites.FundManagementWithdrawalMobile
  : original.FundManagementWithdrawalMobile;
const CashDWEnquiryMobile = lazy(() =>
  import("../../components/FundManagement/CashDWEnquiry.mobile")
);
const ExchangeRecordMobile = lazy(() =>
  import("../../components/FundManagement/ExchangeRecord.mobile")
);
const FundOrderCancelMobile = lazy(() =>
  import("../../components/FundManagement/FundOrderCancel.mobile")
);
const FundHistorical = lazy(() =>
  import("../../companys/ttl/components/Instrument/FundHistorical.mobile")
);
const PositionProportionDialogMobile = lazy(() =>
  import(
    "../../companys/ttl/components/MyAccount/PositionProportion/PositionProportionDialog.mobile"
  )
);
const MarketSetMobile = lazy(() =>
  import("../../companys/ttl/components/Instrument/MarketSet.mobile")
);
const ManageWatchlistGroup = lazy(() =>
  import("../../companys/ttl/components/Instrument/ManageWatchlistGroup.mobile")
);
const DetailCenterMobile = lazy(() =>
  import("../../companys/ttl/components/DetailCenter/DetailCenter.mobile")
);
const BankAccountRegister = lazy(() =>
  import("../../components/PersonalSetting/BankAccountRegister.mobile")
);
const BankAccountStatus = lazy(() =>
  import("../../components/PersonalSetting/BankAccountStatus.mobile")
);
const EDDAEnquiryDialog = lazy(() =>
  import("@/components/PersonalSetting/EDDAEnquiry")
);
const EDDAccountDialog = lazy(() => import("@/components/PersonalSetting/EDDAAccount"));

export const mobileSubLayoutAction = {
  openStockDetail,
  openIPOAction,
  openIPODetail,
  openIPOCancel,
  openCADetail,
  openCashAccountDrawer,
  openPlaceOrder,
  openOrderBook,
  openOrderHistory,
  openPortfolio,
  openTransactionHistory,
  openOrderConfirmStatus,
  openOrderJournalDetail,
  openOrderModification,
  openOrderCancel,
  openPersonalSetting,
  openChangePassword,
  openSwitchAccount,
  openOrderConfirmMobile,
  openFundManagementDWMobile,
  openBankSecuritiesTransfer,
  openExchangeInstruction,
  openFundManagementWithdrawalMobileMobile,
  openCashDWenquiry,
  openExchangeRecord,
  openFundManagementDWModifyMobile,
  openFundManagementWithdrawalModifyMobile,
  openFundOrderCancel,
  openFundHistorical,
  openMarketPositionProportion,
  openMarketSet,
  openManageCustomWatchlistGroup,
  openDetailCenter,
  openBankAccountRegister,
  openBankAccountStatus,
  openEDDAAccountEnquiry,
  openBankAccountSetting,
};

function createChild() {
  let child = document.createElement("div");
  document.body.appendChild(child);
  return child;
}

const removeChild = (child) => {
  ReactDOM.unmountComponentAtNode(child);
  document.body.removeChild(child);
};

const removeprevChild = () => {
  const parent = document.querySelectorAll(
    "div[class*='mobile-sub-layout-container'"
  );
  for (let i = 0; i < parent.length - 1; i++) {
    parent[i].parentNode.remove();
  }
};

class SubLayoutContainer extends React.Component {
  render() {
    const {
      children,
      title,
      target,
      isfundmanage,
      isExchangeInstruction,
      close,
      name,
      stockCode,
    } = this.props;
    return (
      <Provider store={store}>
        <MobileSubLayout
          title={title}
          innerRef={(e) => (this.mobileSubLayoutRef = e)}
          onClose={() => removeChild(target)}
          removeprevChild={() => removeprevChild()}
          isfundmanage={isfundmanage}
          isExchangeInstruction={isExchangeInstruction}
          close={close}
          name={name}
          stockCode={stockCode}>
          {children}
        </MobileSubLayout>
      </Provider>
    );
  }
}

class DrawerLayoutContainer extends React.Component {
  render() {
    const { children, target } = this.props;
    return (
      <Provider store={store}>
        <MobileDrawerLayout onClose={() => removeChild(target)}>
          {/* <Suspense fallback={<Spinner />}> */}
          {children}
          {/* </Suspense> */}
        </MobileDrawerLayout>
      </Provider>
    );
  }
}

function openStockDetail(title) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <InstrumentMobile />
    </SubLayoutContainer>,
    child
  );
}

function openCADetail(title, mode, data, close) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title} close={close}>
      <CADetailMobile mode={mode} data={data} close={close} />
    </SubLayoutContainer>,
    child
  );
}

function openIPOAction(title, mode, data, getBackStep) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title} getBackStep={getBackStep}>
      <ApplyPlan mode={mode} data={data} getBackStep={getBackStep} />
    </SubLayoutContainer>,
    child
  );
}

function openIPODetail(title, data, useIpoAppliedDetailsEnquiryAPI = true) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <AllotmentResultEnquiry
        data={data}
        useIpoAppliedDetailsEnquiryAPI={useIpoAppliedDetailsEnquiryAPI}
        isIPOCancel={false}
      />
    </SubLayoutContainer>,
    child
  );
}

function openIPOCancel(title, data) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <AllotmentResultEnquiry
        data={data}
        useIpoAppliedDetailsEnquiryAPI={true}
        isIPOCancel={true}
      />
    </SubLayoutContainer>,
    child
  );
}

function openPlaceOrder(
  orderData,
  isOther = false,
  isPortfolio = false,
  title = "menu_trade"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <PlaceOrderMobile
        orderData={orderData}
        isOther={isOther}
        isPortfolio={isPortfolio}
        onClose={() => removeChild(child)}
      />
    </SubLayoutContainer>,
    child
  );
}

function openCashAccountDrawer() {
  let child = createChild();
  ReactDOM.render(
    <DrawerLayoutContainer target={child}>
      <SubAccount />
    </DrawerLayoutContainer>,
    child
  );
}

function openOrderBook(title = "order_book_title") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <OrderBookMobile />
    </SubLayoutContainer>,
    child
  );
}

function openOrderHistory(title = "order_history_title") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <OrderHistoryMobile />
    </SubLayoutContainer>,
    child
  );
}

function openPortfolio(title = "portfolio_title") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <PortfolioMobile subLayout={true} />
    </SubLayoutContainer>,
    child
  );
}

function openTransactionHistory(title = "transaction_history_title") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <TransactionHistoryMobile />
    </SubLayoutContainer>,
    child
  );
}

function openOrderConfirmStatus(
  orderResult,
  orderData,
  mode = ORDER_MODE.DETAIL,
  title = "order_cancel_title"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <OrderConfirmStatusMobile
        orderData={orderData}
        orderResult={orderResult}
        mode={mode}
        isCancel="Y"
      />
    </SubLayoutContainer>,
    child
  );
}

function openOrderJournalDetail(
  order,
  closeall = false,
  title = "order_journal_detail_title"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <OrderJournalDetailMobile order={order} closeall={closeall} />
    </SubLayoutContainer>,
    child
  );
}

function openOrderModification(
  order,
  cb = () => {},
  title = "order_modify_title"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <OrderModifyMobile
        order={order}
        refreshData={(isRefresh = false) => cb(isRefresh)}
      />
    </SubLayoutContainer>,
    child
  );
}

function openOrderCancel(order, cb = () => {}, title = "order_cancel_title") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <OrderCancelMobile
        order={order}
        cb={(res, order) => {
          cb(res, order);
        }}
      />
    </SubLayoutContainer>,
    child
  );
}
function openPersonalSetting(
  order,
  cb = () => {},
  title = "personal_setting_title"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <PersonalSetting />
    </SubLayoutContainer>,
    child
  );
}

function openChangePassword(title = "change_password_header") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <ChangePassword />
    </SubLayoutContainer>,
    child
  );
}
function openSwitchAccount(title = "switch_account_header") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <SwitchAccount />
    </SubLayoutContainer>,
    child
  );
}
function openOrderConfirmMobile(
  data,
  isPortfolio,
  title = "order_confirm_title"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <OrderConfirmMobile data={data} isPortfolio={isPortfolio} />
    </SubLayoutContainer>,
    child
  );
}

function openFundManagementDWMobile(
  ccy,
  pageID,
  depositBankAccount,
  withdrawalBankAccount,
  accountBalance,
  isModify,
  modifyFundData,
  settleMethodOption,
  eddaBankAccount,
  isfundmanage,
  title = "fund_management_cash_deposit"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer
      target={child}
      title={title}
      isfundmanage={isfundmanage}>
      <FundManagementDWMobile
        ccy={ccy}
        pageID={pageID}
        depositBankAccount={depositBankAccount}
        withdrawalBankAccount={withdrawalBankAccount}
        accountBalance={accountBalance}
        isModify={isModify}
        modifyFundData={modifyFundData}
        settleMethodOption={settleMethodOption}
        eddaBankAccount={eddaBankAccount}
      />
    </SubLayoutContainer>,
    child
  );
}

function openBankSecuritiesTransfer(
  ccy,
  bankSecuritiesBankAccount,
  accountBalance,
  isModify,
  modifyFundData,
  settleMethodOption,
  title = "fund_management_bank_securities_transfer"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title} isfundmanage={true}>
      <BankSecuritiesTransferMobile
        ccy={ccy}
        bankSecuritiesBankAC={bankSecuritiesBankAccount}
        accountBalance={accountBalance}
        isModify={isModify}
        modifyFundData={modifyFundData}
        settleMethodOption={settleMethodOption}
      />
    </SubLayoutContainer>,
    child
  );
}

function openExchangeInstruction(ccy, title = "fund_management_cash_transfer") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer
      target={child}
      title={title}
      isExchangeInstruction={true}>
      <ExchangeInstructionMobile ccy={ccy} />
    </SubLayoutContainer>,
    child
  );
}
function openFundManagementWithdrawalMobileMobile(
  ccy,
  pageID,
  depositBankAccount,
  withdrawalBankAccount,
  OtherWithdrawalBankAccount,
  accountBalance,
  isModify,
  modifyFundData,
  settleMethodOption,
  eddaBankAccount,
  isfundmanage,
  title = "fund_management_cash_withdrawal"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer
      target={child}
      title={title}
      isfundmanage={isfundmanage}>
      <FundManagementWithdrawalMobile
        ccy={ccy}
        pageID={pageID}
        depositBankAccount={depositBankAccount}
        withdrawalBankAccount={withdrawalBankAccount}
        OtherWithdrawalBankAccount={OtherWithdrawalBankAccount}
        accountBalance={accountBalance}
        isModify={isModify}
        modifyFundData={modifyFundData}
        settleMethodOption={settleMethodOption}
        eddaBankAccount={eddaBankAccount}
      />
    </SubLayoutContainer>,
    child
  );
}

function openCashDWenquiry(title = "cash_dw_enquiry") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <CashDWEnquiryMobile />
    </SubLayoutContainer>,
    child
  );
}

function openExchangeRecord(title = "fund_transfer_enquiry") {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <ExchangeRecordMobile />
    </SubLayoutContainer>,
    child
  );
}

function openFundManagementDWModifyMobile(
  isModify,
  modifyFundData,
  settleMethodOption,
  ccy,
  pageID,
  title = "mobile_modify_cash_deposit"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <FundManagementDWMobile
        isModify={isModify}
        modifyFundData={modifyFundData}
        settleMethodOption={settleMethodOption}
        ccy={ccy}
        pageID={pageID}
      />
    </SubLayoutContainer>,
    child
  );
}

function openFundManagementWithdrawalModifyMobile(
  isModify,
  modifyFundData,
  settleMethodOption,
  ccy,
  pageID,
  title = "mobile_modify_cash_Withdrawal"
) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <FundManagementWithdrawalMobile
        isModify={isModify}
        modifyFundData={modifyFundData}
        settleMethodOption={settleMethodOption}
        ccy={ccy}
        pageID={pageID}
      />
    </SubLayoutContainer>,
    child
  );
}

function openFundOrderCancel(data) {
  let child = createChild();
  const title = "fund_order_cancel_title";
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <FundOrderCancelMobile
        fundOrder={data}
        close={() => {
          removeChild(child);
        }}
      />
    </SubLayoutContainer>,
    child
  );
}

function openFundHistorical(data) {
  let child = createChild();
  const title = "common_funds_historical_nav";
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <FundHistorical
        fundListoricalList={data}
        close={() => {
          removeChild(child);
        }}
      />
    </SubLayoutContainer>,
    child
  );
}

function openMarketPositionProportion() {
  let child = createChild();
  const title = "market_position_proportion_title";
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <PositionProportionDialogMobile
        close={() => {
          removeChild(child);
        }}
      />
    </SubLayoutContainer>,
    child
  );
}

function openMarketSet(tabsWidth, handleRefreshTable) {
  let child = createChild();
  const title = "mobile_edit_watchlists";
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <MarketSetMobile
        tabsWidth={tabsWidth}
        handleRefreshTable={handleRefreshTable}
        close={() => {
          removeChild(child);
        }}
      />
    </SubLayoutContainer>,
    child
  );
}

function openManageCustomWatchlistGroup(tabsWidth) {
  let child = createChild();
  ReactDOM.render(
    <DrawerLayoutContainer target={child}>
      <ManageWatchlistGroup
        close={() => {
          removeChild(child);
        }}
      />
    </DrawerLayoutContainer>,
    child
  );
}

function openDetailCenter(name, stockCode) {
  let child = createChild();
  ReactDOM.render(
    <SubLayoutContainer
      target={child}
      name={name}
      stockCode={stockCode}
      title={null}>
      <DetailCenterMobile
        close={() => {
          removeChild(child);
        }}
      />
    </SubLayoutContainer>,
    child
  );
}

function openBankAccountRegister(withdrawalClose) {
  let child = createChild();
  const title = "new_bank_account_register";
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <BankAccountRegister
        close={() => {
          removeChild(child);
        }}
        withdrawalClose={withdrawalClose}
      />
    </SubLayoutContainer>,
    child
  );
}

function openBankAccountStatus() {
  let child = createChild();
  const title = "bank_account_status_update";
  ReactDOM.render(
    <SubLayoutContainer target={child} title={title}>
      <BankAccountStatus
        close={() => {
          removeChild(child);
        }}
      />
    </SubLayoutContainer>,
    child
  );
}

function openEDDAAccountEnquiry() {
  const child = createChild();
  ReactDOM.render(
    <SubLayoutContainer target={child} title="edda_enquiry_title">
      <EDDAEnquiryDialog isMobile />
    </SubLayoutContainer>,
    child
  );
}

function openBankAccountSetting(isPersonalSetting) {
  const child = createChild();
	ReactDOM.render(
		<SubLayoutContainer target={child} title="edda_registration_title">
			<EDDAccountDialog isMobile isPersonalSetting={isPersonalSetting} />
		</SubLayoutContainer>,
		child
	);
}